import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';
import { Header } from '../components';
import { FaSave, FaTimes } from 'react-icons/fa';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

const TestCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentColor } = useStateContext();
  
  // Get folder ID from URL parameters
  const queryParams = new URLSearchParams(location.search);
  const folderId = queryParams.get('folderId');
  
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [folderDetails, setFolderDetails] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  // Form states
  const [testName, setTestName] = useState('');
  const [testDuration, setTestDuration] = useState(30); // minutes
  const [testPublishStatus, setTestPublishStatus] = useState(false);
  
  // Load folder details
  useEffect(() => {
    if (folderId) {
      fetchFolderDetails();
    } else {
      setError('Folder ID is required to create a test.');
    }
  }, [folderId]);
  
  const fetchFolderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/Folders/${folderId}`);
      setFolderDetails(response.data);
      
    } catch (err) {
      console.error('Error fetching folder details:', err);
      setError('Failed to load folder details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!testName.trim()) {
      setError('Test name is required.');
      return;
    }
    
    try {
      setFormSubmitting(true);
      setError(null);
      
      // Prepare test data according to the model
      const testData = {
        folder_id: parseInt(folderId),
        name: testName,
        publish_status: testPublishStatus,
        time_duration: testDuration,
        created_at: new Date(),
        updated_at: new Date()
      };
      
      console.log('Creating test with data:', testData);
      
      const response = await axios.post(`${API_BASE_URL}/Tests`, testData);
      
      console.log('Test created successfully:', response.data);
      
      // Navigate to the new test or back to folder
      navigate(`/tests/${response.data.test_id}`);
      
    } catch (err) {
      console.error('Error creating test:', err);
      
      if (err.response && err.response.data) {
        setError(`Failed to create test: ${err.response.data.message || 'Unknown error'}`);
      } else {
        setError('Failed to create test. Please try again.');
      }
    } finally {
      setFormSubmitting(false);
    }
  };
  
  const goBack = () => {
    navigate(-1);
  };
  
  // Styles (keeping the existing styles from the previous component)
  const styles = {
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    formRow: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '1rem'
    },
    formLabel: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: '500',
      color: '#374151'
    },
    formInput: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '1rem'
    },
    formCheckboxRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1rem'
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem'
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#F3F4F6',
      color: '#1F2937',
      borderRadius: '0.375rem',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    submitButton: {
      padding: '0.5rem 1.5rem',
      backgroundColor: currentColor,
      color: 'white',
      borderRadius: '0.375rem',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    errorBox: {
      backgroundColor: '#FEE2E2',
      border: '1px solid #F87171',
      color: '#B91C1C',
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      marginBottom: '1rem'
    }
  };
  
  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
      <Header category="Tests" title="Create New Test" />
      
      {/* Error display */}
      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}
      
      {/* Folder information */}
      {folderDetails && (
        <div className="p-4 bg-gray-100 rounded-md mb-6">
          <div className="font-semibold">
            Creating test in folder: {folderDetails.name}
          </div>
          
        </div>
      )}
      
      {/* Test form */}
      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.formRow}>
          <label style={styles.formLabel}>Test Name *</label>
          <input
            type="text"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            style={styles.formInput}
            placeholder="Enter test name"
            required
          />
        </div>
        
        <div style={styles.formRow}>
          <label style={styles.formLabel}>Test Duration (minutes)</label>
          <input
            type="number"
            value={testDuration}
            onChange={(e) => setTestDuration(parseInt(e.target.value) || 0)}
            style={styles.formInput}
            min="1"
            placeholder="Enter test duration"
          />
        </div>
        
        <div style={styles.formCheckboxRow}>
          <input
            type="checkbox"
            id="publishTest"
            checked={testPublishStatus}
            onChange={(e) => setTestPublishStatus(e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="publishTest" style={{ cursor: 'pointer' }}>
            Publish test immediately
          </label>
        </div>
        
        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={goBack}
            style={styles.cancelButton}
          >
            <FaTimes style={{ marginRight: '0.25rem' }} /> Cancel
          </button>
          <button
            type="submit"
            style={styles.submitButton}
            disabled={formSubmitting}
          >
            <FaSave style={{ marginRight: '0.25rem' }} /> Create Test
          </button>
        </div>
      </form>
    </div>
  );
};

export default TestCreate;